import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Link2, Plus, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const normalKarat = (k) => {
  if (!k) return '';
  const s = String(k).toLowerCase().replace(/[^0-9]/g, '');
  return s ? `${s}kt` : '';
};

/**
 * ChainBreakdownPicker — multi-row picker for selecting which chain SKUs and
 * how many of each are needed for an order. Filters available SKUs by the
 * given `karat`. `value` is an array of {chain_sku_id, chain_label, karat, quantity}.
 */
const ChainBreakdownPicker = ({ value, onChange, karat, totalRequired }) => {
  const [allChains, setAllChains] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/chains`)
      .then(r => setAllChains(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAllChains([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const targetK = normalKarat(karat);
    if (!targetK) return allChains;
    return allChains.filter(c => normalKarat(c.karat) === targetK);
  }, [allChains, karat]);

  const breakdownSum = (value || []).reduce((s, b) => s + (parseInt(b.quantity || 0) || 0), 0);
  const unassigned = Math.max(0, parseInt(totalRequired || 0) - breakdownSum);
  const overflow = breakdownSum > parseInt(totalRequired || 0);

  const updateRow = (idx, patch) => {
    const next = [...(value || [])];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const addRow = () => onChange([...(value || []), { chain_sku_id: '', chain_label: '', karat: '', quantity: '' }]);
  const removeRow = (idx) => {
    const next = (value || []).filter((_, i) => i !== idx);
    onChange(next);
  };
  const onSkuChange = (idx, skuId) => {
    const c = filtered.find(x => x.id === skuId);
    if (!c) return updateRow(idx, { chain_sku_id: skuId });
    const label = `${c.chain_type} ${c.length || ''}`.trim();
    updateRow(idx, {
      chain_sku_id: skuId,
      chain_label: label,
      karat: normalKarat(c.karat),
    });
  };

  return (
    <div className="space-y-2" data-testid="chain-breakdown-picker">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Chain Breakdown (by SKU)</Label>
        <div className="flex items-center gap-1 text-[10px]">
          <Badge variant="outline" className={overflow ? 'border-rose-300 text-rose-700' : 'border-border'}>
            Allocated {breakdownSum} / {totalRequired || 0}
          </Badge>
          {unassigned > 0 && <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">{unassigned} unassigned</Badge>}
          {overflow && <Badge variant="outline" className="border-rose-300 text-rose-700 bg-rose-50">overflow</Badge>}
        </div>
      </div>
      {loading ? (
        <p className="text-xs text-muted-foreground">Loading chains…</p>
      ) : filtered.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No chains in inventory{karat ? ` for ${normalKarat(karat)}` : ''}. Add chain SKUs from the Chain Inventory page first.
        </p>
      ) : null}
      {(value || []).map((row, i) => {
        const matched = filtered.find(c => c.id === row.chain_sku_id);
        const inStock = matched?.quantity ?? 0;
        return (
          <div key={i} className="flex items-center gap-2" data-testid={`chain-breakdown-row-${i}`}>
            <Select value={row.chain_sku_id || ''} onValueChange={(v) => onSkuChange(i, v)}>
              <SelectTrigger className="h-8 flex-1" data-testid={`chain-sku-select-${i}`}>
                <SelectValue placeholder="Pick a chain SKU…" />
              </SelectTrigger>
              <SelectContent>
                {filtered.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.chain_type} {c.length || ''} · {normalKarat(c.karat)} · stock {c.quantity}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              min={0}
              className="h-8 w-24"
              value={row.quantity ?? ''}
              onChange={(e) => updateRow(i, { quantity: e.target.value })}
              placeholder="Qty"
              data-testid={`chain-qty-input-${i}`}
            />
            {matched && (
              <Badge variant="outline" className={cn('text-[10px]', parseInt(row.quantity || 0) > inStock ? 'border-rose-300 text-rose-700 bg-rose-50' : 'border-emerald-300 text-emerald-700 bg-emerald-50')}>
                stock {inStock}
              </Badge>
            )}
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-rose-500" onClick={() => removeRow(i)} data-testid={`chain-row-remove-${i}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      })}
      <Button type="button" variant="outline" size="sm" className="h-7" onClick={addRow} data-testid="chain-breakdown-add-row">
        <Plus className="h-3 w-3 mr-1" /> Add chain row
      </Button>
    </div>
  );
};

// Small helper — local cn so we don't pull /lib/utils
const cn = (...c) => c.filter(Boolean).join(' ');

export default ChainBreakdownPicker;
